/**
 * @file   src\components\BankingInfoForm.tsx
 * @brief  BankingInfo Form component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { useState, useRef, useEffect, Row, Col, useParams } from '../utils/thirdpartyComponents';
import CWSelect from './CWSelect';
import CWInput from '../components/CWInput';
import CWRadio from './CWRadio';
import { AddInfoFormPropsBank } from '../interfaces/generalInterface';
import { bankingInfoSchema } from '../validations/bankingInfoSchema';
import * as Yup from 'yup';
import { PaymentType } from '../utils/enums';
import axios from '../config/axiosConfig';
import { CompanyApi } from "../utils/apiUrls";
import { userType } from '../utils/enums';
import FullLoader from '../components/FullLoader';
import { MessageToaster } from "../utils/toastUtil";
import { hasInvalidStripeSecretKey, STRIPE_SECRET_KEY_LIVE_ERROR, stripePublicKeyCwInputLabel, stripeSecretKeyCwInputLabel } from './stripePaymentInputLabels';

const BankingInfoForm: React.FC<AddInfoFormPropsBank> = ({ onValidate, onDataChange, m_formData_bank, cuserType }) => {

    const toaster = new MessageToaster();
    // Convert PaymentType enum to an array of options
    const paymentOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(PaymentType).filter(key => !isNaN(Number(key))).map(key => ({
            label: PaymentType[key as keyof typeof PaymentType],
            value: Number(key)
        }))
    ];
    const [loading, setLoading] = useState(false);
    //State to handle form data
    const [formData, setFormData] = useState({
        account: true,
        PaymentMethod: 1,
        stripeSecretKey: '',
        stripePublicKey: '',
    });

    // State to handle errors
    const [errors, setErrors] = useState<any>({});

    const refs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});


    // On input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        const newFormData = {
            ...formData,
            [name]: value.trim(),
        };

        setFormData(newFormData);
        onDataChange(newFormData);
    };

    //To validate form on clicking next
    const validateForm = async (): Promise<boolean> => {
        if (!formData.account && formData.PaymentMethod === 1 && hasInvalidStripeSecretKey(formData.stripeSecretKey)) {
            toaster.toastError(STRIPE_SECRET_KEY_LIVE_ERROR);
            return false;
        }
        try {
            if(formData.account === false) {
                await bankingInfoSchema.validate(formData, { abortEarly: false });
                setLoading(true)
                const paymentData = {
                    "PaymentMethod" : formData.PaymentMethod,
                    "paymentKeys" : formData
                }
                const { data } = cuserType !== userType['CARWASH-OWNER'] ?
                    await axios.post(CompanyApi.VALIDATE_COMPANY_PAYMENT_API_KEYS, paymentData) :
                    await axios.post(CompanyApi.CARWASH_OWNER_VALIDATE_COMPANY_PAYMENT_API_KEYS, paymentData ) ;
                    setLoading(false)
                    if (data?.error === 0) {
                        setErrors({});
                        return true;
                    } else {
                        toaster.toastError(data?.message);
                        return false;
                    }
            } else {
                setErrors({});
                return true;
            }
            
        } catch (error) {
            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
            return false
        }
    };

    // Validate form
    useEffect(() => {
        onValidate(validateForm);
    }, [formData, onValidate]);

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption?.value || '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        const newFormData = {
            ...formData,
            [name]: value,
        };
        setFormData(newFormData);
        onDataChange(newFormData);
    };

    const onChangeAction = (value: boolean) => {

        if (value === true) {
            formData.stripeSecretKey = '';
            formData.stripePublicKey = '';
            
        } else {
            formData.PaymentMethod = 0;
        }
        const newFormData = {
            ...formData,
            'account': value,
        };

        setFormData(newFormData);
        onDataChange(newFormData);
    };

    useEffect(() => {
        if (m_formData_bank != null) {
            setFormData(m_formData_bank ? m_formData_bank : formData);
        }
    }, [m_formData_bank]);

    // get car wash id
    const param = useParams();
    const carwashid_param = param.id ? parseInt(param.id) : 0;

    // State to company bank details
    const [companybank, setCompanybank] = useState({
        PaymentMethod: 1,
        stripeSecretKey: "",
        stripePublicKey: "",
    });

    // To get company bank details
    const getCompanybank = async () => {
        try {
            let param = '';
            param += carwashid_param;
                    const { data } = cuserType !== userType['CARWASH-OWNER'] ?
                    await axios.get(CompanyApi.GET_COMPANY_BANK_DETAILS + param) :
                    await axios.get(CompanyApi.CARWASH_OWNER_GET_COMPANY_BANK_DETAILS ) ;
                    if (data?.error === 0) {
                        const paymentDetails = data.data.paymentDetails;
                        if(paymentDetails && paymentDetails !== "")
                        {
                            setCompanybank({
                                PaymentMethod: data?.data?.PaymentMethod,
                                stripeSecretKey: paymentDetails.stripeSecretKey,
                                stripePublicKey: paymentDetails.stripePublicKey
                            });
                        }
                        else
                        {
                            setFormData((prevFormData) => ({
                                ...prevFormData,
                                account: false,
                            }));
                        }
                    }

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    useEffect(() => {
        getCompanybank(); // company bank
    }, []);



    return (
        <div className="form-inner mx-auto">
            <div className="adderss-sec">
                <h5 className='f-bold'>Select Payment Type</h5>
                <div className="radio-field-group d-flex">
                    <CWRadio
                        id="washaccount"
                        name="account"
                        label="Use Car Wash Account"
                        checked={formData.account}
                        onChange={() => onChangeAction(true)}
                        disabled={!companybank.stripeSecretKey}
                    />
                    <CWRadio
                        id="newaccount"
                        name="account"
                        label="Add New Account"
                        checked={!formData.account}
                        onChange={() => onChangeAction(false)}
                    />
                </div>
            </div>

            {!formData.account && (
                <>
                    <CWSelect
                        id="PaymentMethod"
                        name="PaymentMethod"
                        options={paymentOptions}
                        label="Select Merchant Payment Type"
                        errorMessage={errors.PaymentMethod && errors.PaymentMethod}
                        dropdownGap={0}
                        dropdownPosition="auto"
                        values={paymentOptions.filter(option => option.value === formData.PaymentMethod)}
                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'PaymentMethod')}
                        placeholder="Select Merchant Payment Type"
                        multi={false}
                        searchable={false}
                        isMandatory
                    />
                    {formData.PaymentMethod > 0 ? <h5 className='mb-4 f-bold'>Merchant Payment Information</h5> : ''}
                    {formData.PaymentMethod === 1 && (
                        <Row>
                            <Col lg={12}>
                                <CWInput
                                    id="stripeSecretKey"
                                    name="stripeSecretKey"
                                    type="text"
                                    label={stripeSecretKeyCwInputLabel}
                                    value={formData.stripeSecretKey}
                                    onChange={handleChange}
                                    errorMessage={errors.stripeSecretKey}
                                    ref={(el) => (refs.current.stripeSecretKey = el)}
                                    autoComplete="off"
                                    isMandatory
                                    autoFocus
                                />
                            </Col>
                            <Col lg={12}>
                                <CWInput
                                    id="stripePublicKey"
                                    name="stripePublicKey"
                                    type="text"
                                    label={stripePublicKeyCwInputLabel}
                                    value={formData.stripePublicKey}
                                    onChange={handleChange}
                                    errorMessage={errors.stripePublicKey}
                                    isMandatory
                                    ref={(el) => (refs.current.stripePublicKey = el)}
                                />
                            </Col>
                        </Row>
                        )}
                        {formData.PaymentMethod === 2 && (
                        <Row>
                        {/* use when world pay is ready */}
                            <Col lg={12}>
                                <CWInput
                                    id="SecretKey"
                                    name="SecretKey"
                                    type="text"
                                    label="SecretKey"
                                    onChange={handleChange}
                                    // value={formData.SecretKey}
                                    // errorMessage={errors.SecretKey}
                                    // ref={(el) => (refs.current.SecretKey = el)}
                                    autoComplete="off"
                                    isMandatory
                                    autoFocus
                                />
                            </Col>
                            <Col lg={12}>
                                <CWInput
                                    id="PublicKey"
                                    name="PublicKey"
                                    type="text"
                                    label="Account Name"
                                    onChange={handleChange}
                                    // value={formData.PublicKey}
                                    // errorMessage={errors.PublicKey}
                                    // ref={(el) => (refs.current.PublicKey = el)}
                                    isMandatory
                                />
                            </Col>
                        </Row>
                        )}
                </>
            )}

            {formData.account && (
                <>
                    <CWInput
                        id="bankName"
                        name="bankName"
                        type="text"
                        label="Merchant Payment Type"
                        value={companybank.PaymentMethod === 1 ? "Stripe" : "WorldPay"}
                        isMandatory
                        ref={(el) => (refs.current.bankName = el)}
                        disabled
                    />
                    <h5 className='mb-4 f-bold'>Merchant Payment Information</h5>
                    <Row>
                        <Col lg={12}>
                            <CWInput
                                id="stripeSecretKey"
                                name="stripeSecretKey"
                                type="text"
                                label={stripeSecretKeyCwInputLabel}
                                value={companybank.stripeSecretKey}
                                autoComplete="off"
                                disabled
                            />
                        </Col>
                        <Col lg={12}>
                            <CWInput
                                id="stripePublicKey"
                                name="stripePublicKey"
                                type="text"
                                label={stripePublicKeyCwInputLabel}
                                value={companybank.stripePublicKey}
                                autoComplete="off"
                                disabled
                            />
                        </Col>
                    </Row>
                </>
            )}
             {loading && <FullLoader />}
        </div>

    )
}
export default BankingInfoForm;
